import Bookings from '../pages/patientTransport/Bookings'
import Control from '../pages/patientTransport/Control'
import CrewView from '../pages/patientTransport/CrewView'
import HospitalPortal from '../pages/patientTransport/HospitalPortal'
import Invoicing from '../pages/patientTransport/Invoicing'
import LiveTracking from '../pages/patientTransport/LiveTracking'
import MobileApp from '../pages/patientTransport/MobileApp'
import Organisations from '../pages/patientTransport/Organisations'
import Overview from '../pages/patientTransport/Overview'
import Patients from '../pages/patientTransport/Patients'
import Planning from '../pages/patientTransport/Planning'
import Reports from '../pages/patientTransport/Reports'
import RiskAssessments from '../pages/patientTransport/RiskAssessments'

export default [
  {
    id: 'overview',
    title: 'Overview',
    pages: [
      {
        id: 'overview',
        title: 'PTS Overview',
        summary:
          'Understand how Patient Transport joins patients, organisations, bookings, journeys, duties (including dedicated discharge crews), routes, crew work, dispatch, and the hospital portal — and the access model (Co-Ordinator is the only PTS permission; crew screens are open to all staff).',
        component: Overview,
        tags: [
          'PTS',
          'Overview',
          'Workflow',
          'Journeys',
          'Duties',
          'Portal',
          'Permissions',
          'Access',
        ],
      },
    ],
  },
  {
    id: 'records-and-bookings',
    title: 'Records and Bookings',
    pages: [
      {
        id: 'patients',
        title: 'Patients',
        summary:
          'Create and edit patient records, manage patient identifiers, demographics, mobility needs, height and weight, and clinical or risk factors — with a reduced name-only minimum for tenants that enable it.',
        component: Patients,
        tags: [
          'PTS',
          'Patients',
          'Patient Records',
          'NHS Number',
          'Mobility',
          'Height',
          'Weight',
          'Minimum Dataset',
        ],
      },
      {
        id: 'risk-assessments',
        title: 'Risk Assessments',
        summary:
          'Structured patient transport risk assessments: per-domain ratings, transport requirements, actions, PDF export, controlled sharing with the hospital portal, and crew on-scene completion via the Risk Assessment visit journey type.',
        component: RiskAssessments,
        tags: [
          'PTS',
          'Risk Assessments',
          'Risk',
          'Patients',
          'Discharge',
          'Hospital Portal',
          'PDF',
          'Risk Assessment Visits',
          'Crew',
        ],
      },
      {
        id: 'bookings',
        title: 'Bookings',
        summary:
          'Create, search, edit, and plan patient transport bookings: date presets and status tiles, patient lookup, the reduced minimum dataset (create now, complete later with outstanding-details flags), journey types, customer-aware pickup/dropoff and per-stop hospital selection, saved requester contacts, return journeys, recurrence, eligibility screening, billing setup, invoiced-booking locking, journey cancellation, and the outstanding-journeys banner.',
        component: Bookings,
        tags: [
          'PTS',
          'Bookings',
          'Booking',
          'Transport',
          'Journey',
          'Pickup',
          'Dropoff',
          'Recurrence',
          'Eligibility',
          'Outstanding Journeys',
          'Two-Factor',
          'Minimum Dataset',
          'Outstanding Details',
          'Contacts',
          'Requested By',
          'Risk Assessment Visits',
          'Crew Jobs',
        ],
      },
    ],
  },
  {
    id: 'planning-and-dispatch',
    title: 'Planning and Dispatch',
    pages: [
      {
        id: 'planning',
        title: 'Planning',
        summary:
          'Create PTS duties, assign shifts and vehicles (with crew acknowledgement), arrange routes, use route suggestions, allocate unassigned journeys, advertise open shifts, set up dedicated discharge-crew duties (no-dispatch and whole-duty billing), and the Vehicle/Patient Timeline gantt views.',
        component: Planning,
        tags: [
          'PTS',
          'Planning',
          'Duties',
          'Routes',
          'Shifts',
          'Vehicles',
          'Timeline',
          'Patient Timeline',
          'Dedicated Duties',
          'Discharge Crews',
          'Whole-Duty Billing',
        ],
      },
      {
        id: 'control',
        title: 'Control and Dispatch',
        summary:
          'Use the PTS dispatch view to monitor duties, routes, unassigned journeys, maps, live crew ETAs, and journey status — plus crew location-sharing consent, vehicle changes, exception handling, and ending duties.',
        component: Control,
        tags: [
          'PTS',
          'Control',
          'Dispatch',
          'Routes',
          'Map',
          'Duties',
          'ETA',
          'Consent',
          'Location',
          'End Duty',
        ],
      },
    ],
  },
  {
    id: 'crew-and-tracking',
    title: 'Crew and Tracking',
    pages: [
      {
        id: 'crew-view',
        title: 'Crew View',
        summary:
          "The crew-facing PTS screen, open to every employee: today's journeys, working through a journey's status (including risk-assessment visits), arrival confirmation, sat-nav handoff, adding your own jobs on dedicated no-dispatch duties, control room chat, delay reporting, consent-based location sharing, and change notifications.",
        component: CrewView,
        tags: [
          'PTS',
          'Crew',
          'Crew View',
          'Driver',
          'Journeys',
          'Chat',
          'Notifications',
          'Add a Job',
          'Dedicated Duties',
          'Risk Assessment Visits',
        ],
      },
      {
        id: 'mobile-app',
        title: 'Mobile App',
        summary:
          "The native AmbuNet mobile app's PTS screen, open to every employee: today's duty and journeys, the My Duties list with tap-through journey details, working a journey's status, GPS-based arrival confirmation, sat-nav handoff, adding your own jobs on dedicated no-dispatch duties, control room chat, and approving location-sharing requests.",
        component: MobileApp,
        tags: [
          'PTS',
          'Mobile',
          'Mobile App',
          'Crew',
          'Driver',
          'Journeys',
          'Chat',
          'Location',
          'Add a Job',
          'My Duties',
        ],
      },
      {
        id: 'live-tracking',
        title: 'Live Journey Tracking',
        summary:
          "The no-login public tracking link sent to patients and wards once a crew is on the way — the live ETA from the vehicle's shared position (with its seed-estimate fallback), how to turn it on, and its limitations.",
        component: LiveTracking,
        tags: [
          'PTS',
          'Live Tracking',
          'ETA',
          'Live ETA',
          'Notifications',
          'SMS',
          'Public Link',
        ],
      },
    ],
  },
  {
    id: 'portal-and-organisations',
    title: 'Portal and Organisations',
    pages: [
      {
        id: 'hospital-portal',
        title: 'Hospital Booking Portal',
        summary:
          'Configure and support hospital portal users, ward/site/admin access, planned work views with live crew-tracking ETAs, booking requests (including auto-approved dedicated-crew bookings), and live control room chat.',
        component: HospitalPortal,
        tags: [
          'PTS',
          'Hospital Portal',
          'Wards',
          'Access',
          'Chat',
          'Requests',
          'Dedicated Crew',
          'ETA',
        ],
      },
      {
        id: 'organisations',
        title: 'Organisations and Users',
        summary:
          'Add and manage PTS organisations, wards, departments, hospital portal users, requester details, organisation-linked booking information, the Organisations Hub dashboard, contract/tariff/SLA settings, and the SLA performance pack.',
        component: Organisations,
        tags: [
          'PTS',
          'Organisations',
          'Users',
          'Wards',
          'Requesters',
          'Hospitals',
          'Contract',
          'SLA',
          'Performance',
        ],
      },
    ],
  },
  {
    id: 'finance-and-reporting',
    title: 'Finance and Reporting',
    pages: [
      {
        id: 'invoicing',
        title: 'Quoting, Invoicing & Billing',
        summary:
          'Tariffs, mileage charging (base-to-base and per-journey set mileage), quotes, auto-priced invoices, invoiced-booking locking, billing runs (per-journey and whole-duty for dedicated crews), credit notes, and the Xero export for PTS.',
        component: Invoicing,
        tags: [
          'PTS',
          'Invoicing',
          'Billing',
          'Quotes',
          'Tariffs',
          'Mileage',
          'Base to Base',
          'Set Mileage',
          'Mileage Billing',
          'Credit Notes',
          'Xero',
          'Billing Runs',
          'Whole-Duty Billing',
          'Dedicated Duties',
        ],
      },
      {
        id: 'reports',
        title: 'Reports & Performance',
        summary:
          'On-time pickup/arrival performance, portal-booking share, delay reasons, and SLA reporting for Patient Transport.',
        component: Reports,
        tags: [
          'PTS',
          'Reports',
          'Performance',
          'On-Time',
          'KPI',
          'SLA',
          'Delays',
        ],
      },
    ],
  },
]
